
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shop } from '../domain/types';

interface ShopsExplorerRailProps {
  shops: Shop[];
  isLoading?: boolean;
}

const PlaceholderShopCard = () => (
  <div className="flex-shrink-0 w-32 text-center group">
    <div className="w-24 h-24 mx-auto rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-white/5 dark:to-white/10 border-2 border-dashed border-gray-300 dark:border-white/20 flex flex-col items-center justify-center p-2">
      <span className="material-symbols-outlined text-2xl text-gray-400 dark:text-gray-500 mb-1">storefront</span>
      <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">Próximamente</span>
    </div>
    <div className="mt-2 h-3 w-16 mx-auto bg-gray-200 dark:bg-white/10 rounded animate-pulse"></div>
  </div>
);

export const ShopsExplorerRail = React.memo(({ shops, isLoading = false }: ShopsExplorerRailProps) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="py-6 bg-gradient-to-b from-transparent to-gray-50/30 dark:to-white/5">
        <div className="px-4 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-white/10 animate-pulse"></div>
            <div className="h-5 w-40 bg-gray-200 dark:bg-white/10 rounded animate-pulse"></div>
          </div>
          <div className="h-3 w-56 bg-gray-200 dark:bg-white/10 rounded animate-pulse"></div>
        </div>
        <div className="flex gap-4 overflow-hidden px-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex-shrink-0 w-32 text-center">
              <div className="w-24 h-24 mx-auto rounded-2xl bg-gray-200 dark:bg-white/10 animate-pulse"></div>
              <div className="mt-2 h-3 w-16 mx-auto bg-gray-200 dark:bg-white/10 rounded animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const displayShops = shops.length > 0 ? shops : [];
  const showPlaceholders = shops.length === 0;

  return (
    <div className="py-6 bg-gradient-to-b from-transparent via-gray-50/50 to-gray-50/80 dark:via-white/5 dark:to-white/10">
      <div className="px-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="material-symbols-outlined text-xl text-primary">storefront</span>
              <h2 className="text-xl font-black tracking-tight text-text-main dark:text-white leading-none">
                Explorar Tiendas
              </h2>
            </div>
            <p className="text-xs text-text-secondary dark:text-gray-400 font-medium">
              {showPlaceholders 
                ? 'Descubrí las mejores tiendas de Tucumán' 
                : 'Descubrí vendedores únicos en Tucumán'}
            </p>
          </div>
          <button
            onClick={() => navigate('/tiendas')}
            className="text-xs font-bold text-primary hover:text-primary-dark dark:hover:text-primary-light transition-colors flex items-center gap-0.5 group"
          >
            Ver todas
            <span className="material-symbols-outlined text-sm group-hover:translate-x-0.5 transition-transform">chevron_right</span>
          </button>
        </div>
      </div>

      <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4">
        {showPlaceholders ? (
          <>
            <PlaceholderShopCard />
            <PlaceholderShopCard />
            <PlaceholderShopCard />
            <PlaceholderShopCard />
            <PlaceholderShopCard />
          </>
        ) : (
          displayShops.map(shop => (
            <button
              key={shop.id}
              onClick={() => navigate(`/tienda/${shop.slug}`)}
              className="flex-shrink-0 w-32 text-center group"
            >
              <div className="w-24 h-24 mx-auto rounded-2xl overflow-hidden border-2 border-gray-200 dark:border-white/10 shadow-sm group-hover:shadow-lg group-hover:border-primary/50 group-hover:scale-105 transition-all duration-300 bg-white dark:bg-white/5">
                {shop.logo ? (
                  <img 
                    src={shop.logo} 
                    alt={shop.name} 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary/10 to-primary/20 flex items-center justify-center">
                    <span className="material-symbols-outlined text-3xl text-primary/60">storefront</span>
                  </div>
                )}
              </div>
              <p className="mt-2 text-xs font-bold text-text-main dark:text-white line-clamp-1 group-hover:text-primary transition-colors">
                {shop.name}
              </p>
              {shop.stats?.rating ? (
                <div className="flex items-center justify-center gap-0.5 mt-1">
                  <span className="material-symbols-outlined text-yellow-500 text-xs filled">star</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{shop.stats.rating.toFixed(1)}</span>
                  {shop.stats?.reviewCount && (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">({shop.stats.reviewCount})</span>
                  )}
                </div>
              ) : (
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">Nueva tienda</p>
              )}
            </button>
          ))
        )}
      </div>

      {/* Decorative bottom gradient */}
      <div className="h-px mt-4 mx-4 bg-gradient-to-r from-transparent via-gray-200 dark:via-white/10 to-transparent"></div>
    </div>
  );
});

ShopsExplorerRail.displayName = 'ShopsExplorerRail';
